import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError } from "@/lib/format-api-error";
import type { FundAllocationDetail } from "./types";
import { useUpdateAllocation } from "./use-update-allocation";

// Sub-bucket 15 — port of /app/admin/mdf/[id]/_components/EditAllocationDialog.tsx.
// partner_id is fixed at create (the DB has unique(fund,partner); to switch
// partners admins remove + re-add). Only cap + notes are editable.
//
// If the new cap would drop the balance negative (cap < used), the user is
// warned but allowed to proceed — the service enforces no further constraint.

export function EditAllocationDialog({
  fundId,
  allocation,
  open,
  onOpenChange,
}: {
  fundId: string;
  allocation: FundAllocationDetail;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const update = useUpdateAllocation();
  const [cap, setCap] = useState(String(allocation.cap_amount_eur));
  const [notes, setNotes] = useState(allocation.notes ?? "");

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const capParsed = Number(cap);
    if (!Number.isInteger(capParsed) || capParsed < 0) {
      toast.error("Cap must be a whole number of EUR");
      return;
    }
    if (capParsed < allocation.used_amount_eur) {
      const proceed = window.confirm(
        `Reducing the cap below committed spend (€${allocation.used_amount_eur}) will leave the balance negative. Continue?`,
      );
      if (!proceed) return;
    }
    update.mutate(
      {
        id: allocation.id,
        fundId,
        input: {
          cap_amount_eur: capParsed,
          notes: notes.trim() ? notes.trim() : undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success("Allocation updated");
          onOpenChange(false);
        },
        onError: (err) =>
          toast.error(formatApiError(err, "Failed to update allocation")),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Edit allocation
          </DialogTitle>
          <DialogDescription>
            {allocation.partner_name ?? "Unknown partner"} — currently using €
            {allocation.used_amount_eur}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="mdf-edit-alloc-cap">Cap (EUR)</Label>
            <Input
              id="mdf-edit-alloc-cap"
              type="number"
              min={0}
              step={1}
              value={cap}
              onChange={(e) => setCap(e.target.value)}
              required
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="mdf-edit-alloc-notes">Notes</Label>
            <Textarea
              id="mdf-edit-alloc-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={update.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
